import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    ChevronDown,
    ChevronUp,
    FlipHorizontal2,
    FlipVertical2,
    LayoutDashboard,
    Trash2,
    Wand2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { ProductNodeProduct } from './ProductNode';
import { AspectRatioControl } from './AspectRatioControl';

/**
 * Arrange products on a flat board, and let the renderer build the room.
 *
 * The 3D studio answers "where is the camera"; this answers "what is in the
 * room". A board carries no perspective at all — and it does not need to.
 * Measured across three boards: the model infers DEPTH from vertical position
 * (pieces higher up land further back) and reconciles cut-outs shot from
 * different angles into one camera on its own. What it cannot infer is a
 * ground plane, which is why the worker pads blank floor under every board.
 *
 * So the vendor's job is arrangement, and the controls serve only that.
 */

/** Normalised to the board, so a layout survives a different output size. */
export interface BoardItem {
    id: string;
    uuid: string;
    name: string;
    src: string;
    /** Centre, as 0..1 of board width/height. */
    x: number;
    y: number;
    /** Width as a fraction of the board; height follows the image ratio. */
    w: number;
    /** Degrees clockwise. */
    rot: number;
    flipX?: boolean;
    flipY?: boolean;
}

export interface MoodboardState {
    v: 1;
    items: BoardItem[];
    aspect: string;
}

/** Long side of the composited board. The renderer works at 1024. */
const EXPORT_LONG_EDGE = 1280;

/** Offered when the recipe does not supply its own list. */
const FALLBACK_ASPECTS = [
    { value: '1:1' }, { value: '4:5' }, { value: '3:4' },
    { value: '9:16' }, { value: '4:3' }, { value: '16:9' },
];

/**
 * The board's ground colour, on screen AND in the exported PNG — the two must
 * match exactly, so it stays a literal rather than a theme token. A plain
 * near-white board: the renderer treats empty area as space to build in, and
 * a coloured ground would read as a material choice.
 */
const BOARD_GROUND = '#F6F4F1';

function aspectOf(ratio?: string): number {
    const [w, h] = (ratio || '3:4').split(':').map(Number);
    return w && h ? w / h : 0.75;
}

/**
 * A first arrangement, so an opened board is never an empty rectangle.
 *
 * Later pieces sit lower, which is both how a room reads and how the renderer
 * interprets the board, since vertical position becomes depth.
 */
function autoLayout(products: ProductNodeProduct[], srcOf: (p: ProductNodeProduct) => string): BoardItem[] {
    const usable = products.filter((p) => srcOf(p));
    return usable.map((p, i) => {
        const col = i % 2;
        const row = Math.floor(i / 2);
        const perRow = Math.min(2, usable.length);
        return {
            id: `bi-${i}-${p.uuid.slice(0, 6)}`,
            uuid: p.uuid,
            name: p.name,
            src: srcOf(p),
            x: perRow === 1 ? 0.5 : 0.28 + col * 0.44,
            y: Math.min(0.86, 0.26 + row * 0.28),
            w: usable.length <= 2 ? 0.6 : 0.42,
            rot: 0,
        };
    });
}

/** One icon button in the selected piece's action bar. */
const BarButton: React.FC<{
    label: string;
    onClick: () => void;
    danger?: boolean;
    busy?: boolean;
    children: React.ReactNode;
}> = ({ label, onClick, danger, busy, children }) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <button
                type="button"
                onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
                onClick={onClick}
                aria-label={label}
                className={cn(
                    'flex size-6 items-center justify-center border-0 bg-transparent p-0 transition-colors hover:bg-background/20 [&_svg]:size-[15px]',
                    danger ? 'text-destructive' : 'text-background',
                    busy ? 'cursor-wait opacity-50' : 'cursor-pointer',
                )}
            >
                {children}
            </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
);

export const MoodboardComposer: React.FC<{
    products: ProductNodeProduct[];
    aspectRatio?: string;
    aspectOptions?: Array<{ value: string; label?: string }>;
    onAspectRatioChange?: (v: string) => void;
    snapshotUrl?: string;
    board?: MoodboardState | null;
    onBoardChange?: (b: MoodboardState) => void;
    onCapture: (file: File) => Promise<string>;
    onSnapshotUpdated: (url: string) => void;
    /** Store an imported/pasted image and return a durable URL for it. */
    onUploadImage?: (file: File) => Promise<string>;
    /** Cut the subject out of an image, returning a transparent PNG URL. */
    onRemoveBackground?: (imageUrl: string) => Promise<string>;
    /** Open the catalogue so more products join this node — and this board. */
    onAddProducts?: () => void;
}> = ({
    products, aspectRatio, aspectOptions, onAspectRatioChange, snapshotUrl, board, onBoardChange,
    onCapture, onSnapshotUpdated, onUploadImage, onRemoveBackground, onAddProducts,
}) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    /** The product photo a board cut-out is made from. */
    const srcOf = useCallback((p: ProductNodeProduct): string => (
        p.originalImagePath || p.thumbnailPath || (p.galleryImages || [])[0] || ''
    ), []);

    const [items, setItems] = useState<BoardItem[]>(board?.items || []);
    const [selected, setSelected] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const boardRef = useRef<HTMLDivElement | null>(null);
    const importRef = useRef<HTMLInputElement>(null);
    const [cutting, setCutting] = useState<string | null>(null);
    /** The newest arrangement, readable synchronously — state is not. */
    const latest = useRef<BoardItem[]>(board?.items || []);
    const ratio = aspectOf(aspectRatio);

    /**
     * Seed from the node's products, ONCE per opening.
     *
     * Depending on `products` here re-ran the effect on every parent render (it
     * arrives as a possibly-new array), and when nothing was placeable the
     * fresh empty array it set changed state by identity alone — an infinite
     * loop React stops with "Maximum update depth exceeded".
     */
    const seeded = useRef(false);
    useEffect(() => {
        if (!open) { seeded.current = false; return; }
        if (seeded.current) return;
        seeded.current = true;
        if (items.length || !products.length) return;
        const laid = autoLayout(products, srcOf);
        if (laid.length) { latest.current = laid; setItems(laid); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    /** Every mutation goes through here, so the ref and the parent never drift. */
    const persist = useCallback((next: BoardItem[]) => {
        latest.current = next;
        setItems(next);
        onBoardChange?.({ v: 1, items: next, aspect: aspectRatio || '3:4' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onBoardChange, aspectRatio]);

    const patch = (id: string, change: Partial<BoardItem>) => {
        persist(latest.current.map((i) => (i.id === id ? { ...i, ...change } : i)));
    };

    /** Drag, resize and rotate are one gesture — all are pointer deltas. */
    const startGesture = (e: React.PointerEvent, id: string, mode: 'move' | 'size' | 'rotate') => {
        e.preventDefault();
        e.stopPropagation();
        const rect = boardRef.current?.getBoundingClientRect();
        if (!rect) return;
        setSelected(id);
        const start = latest.current.find((i) => i.id === id);
        if (!start) return;
        const px = e.clientX;
        const py = e.clientY;
        // Computed from the arrangement as it was when the gesture began, so the
        // state updater never has to read previous state and can stay pure.
        const base = latest.current;

        const onMove = (ev: PointerEvent) => {
            const dx = (ev.clientX - px) / rect.width;
            const dy = (ev.clientY - py) / rect.height;
            const next = base.map((i) => {
                if (i.id !== id) return i;
                if (mode === 'move') {
                    return {
                        ...i,
                        x: Math.max(0.04, Math.min(0.96, start.x + dx)),
                        y: Math.max(0.04, Math.min(0.96, start.y + dy)),
                    };
                }
                if (mode === 'size') {
                    // Uniform: a stretched sofa is one the renderer must un-distort.
                    return { ...i, w: Math.max(0.08, Math.min(1.2, start.w + dx * 2)) };
                }
                const cx = rect.left + start.x * rect.width;
                const cy = rect.top + start.y * rect.height;
                const deg = (Math.atan2(ev.clientY - cy, ev.clientX - cx) * 180) / Math.PI;
                // The handle sits above the piece, so that direction is zero.
                const raw = Math.round(deg + 90);
                // Snap near the cardinals: an almost-upright sofa is a mistake.
                const snapped = Math.abs(raw % 90) < 4 ? Math.round(raw / 90) * 90 : raw;
                return { ...i, rot: ((snapped % 360) + 360) % 360 };
            });
            latest.current = next;
            setItems(next);
        };
        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            // Persisted outside any updater: calling the parent's setState from
            // inside one schedules an update during render.
            onBoardChange?.({ v: 1, items: latest.current, aspect: aspectRatio || '3:4' });
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };

    const removeItem = (id: string) => {
        persist(latest.current.filter((i) => i.id !== id));
        if (selected === id) setSelected(null);
    };

    /**
     * Stacking order IS array order — later draws on top.
     *
     * Which also makes it the layer list, read backwards: the front of the board
     * is the top of the panel, the way every layers panel behaves.
     */
    const move = (id: string, dir: -1 | 1) => {
        const list = [...latest.current];
        const i = list.findIndex((x) => x.id === id);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= list.length) return;
        [list[i], list[j]] = [list[j], list[i]];
        persist(list);
    };

    const addProduct = (p: ProductNodeProduct) => {
        const src = srcOf(p);
        if (!src) return;
        persist([...latest.current, {
            id: `bi-${Date.now().toString(36)}`,
            uuid: p.uuid,
            name: p.name,
            src,
            x: 0.5,
            y: 0.6,
            w: 0.4,
            rot: 0,
        }]);
    };

    /**
     * Bring in an image that is not one of the vendor's products.
     *
     * Uploaded rather than kept as a blob URL: the arrangement is persisted on
     * the node, so a board referring to an object URL comes back empty after a
     * reload — the trap the studio scene hit.
     */
    const importFile = useCallback(async (file: File) => {
        if (!file.type.startsWith('image/')) return;
        setError(null);
        try {
            const url = onUploadImage ? await onUploadImage(file) : URL.createObjectURL(file);
            const next = [...latest.current, {
                id: `bi-${Date.now().toString(36)}`,
                uuid: '',
                name: file.name.replace(/\.[^.]+$/, ''),
                src: url,
                x: 0.5, y: 0.5, w: 0.4, rot: 0,
            }];
            latest.current = next;
            setItems(next);
            onBoardChange?.({ v: 1, items: next, aspect: aspectRatio || '3:4' });
        } catch (err: any) {
            setError(err?.message || 'Could not import that image');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onUploadImage, onBoardChange, aspectRatio]);

    /** Paste an image straight onto the board. */
    useEffect(() => {
        if (!open) return;
        const onPaste = (e: ClipboardEvent) => {
            const entries = Array.from(e.clipboardData?.items || []);
            const img = entries.find((i) => i.type.startsWith('image/'));
            if (!img) return;
            const file = img.getAsFile();
            if (!file) return;
            e.preventDefault();
            void importFile(file);
        };
        const handler = onPaste as EventListener;
        window.addEventListener('paste', handler);
        return () => window.removeEventListener('paste', handler);
    }, [open, importFile]);

    /** Cut the background off one placed piece. */
    const cutOut = async (it: BoardItem) => {
        if (!onRemoveBackground) return;
        setCutting(it.id);
        setError(null);
        try {
            const url = await onRemoveBackground(it.src);
            patch(it.id, { src: url });
        } catch (err: any) {
            setError(err?.message || 'Could not remove the background');
        } finally {
            setCutting(null);
        }
    };

    /**
     * Flatten the board to a PNG and hand it upstream.
     *
     * The bucket serves `access-control-allow-origin: *`, so the cut-outs can be
     * drawn into a canvas and read back — without that the canvas would taint
     * and `toBlob` would throw instead of returning a board.
     */
    const captureBoard = async () => {
        setBusy(true);
        setError(null);
        try {
            const W = ratio >= 1 ? EXPORT_LONG_EDGE : Math.round(EXPORT_LONG_EDGE * ratio);
            const H = ratio >= 1 ? Math.round(EXPORT_LONG_EDGE / ratio) : EXPORT_LONG_EDGE;
            const canvas = document.createElement('canvas');
            canvas.width = W;
            canvas.height = H;
            const ctx = canvas.getContext('2d');
            if (!ctx) throw new Error('canvas unavailable');
            ctx.fillStyle = BOARD_GROUND;
            ctx.fillRect(0, 0, W, H);

            const loaded = await Promise.all(latest.current.map((it) => new Promise<{ it: BoardItem; img: HTMLImageElement } | null>((resolve) => {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => resolve({ it, img });
                // One bad URL must not cost the whole board.
                img.onerror = () => resolve(null);
                img.src = it.src;
            })));

            for (const entry of loaded) {
                if (!entry) continue;
                const { it, img } = entry;
                const w = it.w * W;
                const h = w * (img.naturalHeight / Math.max(1, img.naturalWidth));
                // Rotate and flip about the piece's own centre, so the flattened
                // board matches the arrangement on screen exactly.
                ctx.save();
                ctx.translate(it.x * W, it.y * H);
                if (it.rot) ctx.rotate((it.rot * Math.PI) / 180);
                if (it.flipX || it.flipY) ctx.scale(it.flipX ? -1 : 1, it.flipY ? -1 : 1);
                ctx.drawImage(img, -w / 2, -h / 2, w, h);
                ctx.restore();
            }

            const blob: Blob | null = await new Promise((r) => canvas.toBlob((b) => r(b), 'image/png'));
            if (!blob) throw new Error('could not read the board back');
            const url = await onCapture(new File([blob], `moodboard-${Date.now()}.png`, { type: 'image/png' }));
            onSnapshotUpdated(url);
            setOpen(false);
        } catch (err: any) {
            setError(err?.message || 'Could not save the board');
        } finally {
            setBusy(false);
        }
    };

    const usableProducts = useMemo(() => products.filter((p) => srcOf(p)), [products, srcOf]);
    const selectedItem = items.find((i) => i.id === selected) || null;

    return (
        <>
            {/**
             * The node's launcher, deliberately identical to the 3D studio's.
             *
             * They are two routes to the same thing, so they should read as one
             * control with two modes — same square tile, same floating pill,
             * same hover. Only the icon and the words differ.
             */}
            <div
                className="group relative flex aspect-square w-full cursor-pointer flex-col items-center justify-center gap-2.5 overflow-hidden rounded-lg border bg-muted transition-colors hover:border-input"
                onClick={() => setOpen(true)}
                onMouseDown={(e) => e.stopPropagation()}
            >
                {snapshotUrl ? (
                    <img
                        src={snapshotUrl}
                        alt="Moodboard"
                        className="absolute inset-0 h-full w-full object-contain"
                    />
                ) : (
                    <div className="text-muted-foreground opacity-40 [&_svg]:block">
                        {/* A board with pieces laid on it, drawn in the same
                            outlined weight as the studio's cube. */}
                        <svg width="46" height="46" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="5" y="5" width="38" height="38" rx="2.5" />
                            <rect x="10.5" y="10.5" width="14" height="11" rx="1.5" />
                            <rect x="28.5" y="10.5" width="9" height="17" rx="1.5" />
                            <rect x="10.5" y="26.5" width="19" height="11" rx="1.5" />
                        </svg>
                    </div>
                )}
                <div className="absolute bottom-2.5 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-lg border bg-background/90 px-2.5 py-1 text-xs font-medium whitespace-nowrap text-foreground backdrop-blur-md transition-colors group-hover:border-input">
                    <LayoutDashboard className="size-3.5" />
                    {snapshotUrl
                        ? t('CONTENT_STUDIO.MOODBOARD_REARRANGE', 'Rearrange')
                        : t('CONTENT_STUDIO.MOODBOARD_OPEN', 'Open moodboard')}
                </div>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent
                    /**
                     * A right-click here was opening the CANVAS context menu.
                     *
                     * The dialog portals to <body>, but React events bubble
                     * along the REACT tree, not the DOM one — and this dialog is
                     * rendered inside the recipe node, inside the canvas. So the
                     * canvas saw every right-click as its own.
                     */
                    onContextMenu={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); }}
                    onDragOver={(e: React.DragEvent) => { e.preventDefault(); }}
                    onDrop={(e: React.DragEvent) => {
                        const file = Array.from(e.dataTransfer?.files || [])[0];
                        if (file) { e.preventDefault(); e.stopPropagation(); void importFile(file); }
                    }}
                    className="flex h-[min(760px,86vh)] w-[min(1180px,96vw)] max-w-none flex-row gap-0 overflow-hidden p-0 sm:max-w-none"
                >
                    {/* Radix needs a name for the dialog. This one is a full
                        workspace with no title bar, so it is announced only. */}
                    <DialogTitle className="sr-only">{t('CONTENT_STUDIO.MOODBOARD_OPEN')}</DialogTitle>
                    {/* Sources — the catalogue on this node, plus anything imported. */}
                    <div className="w-52 shrink-0 overflow-y-auto border-e p-3">
                        <input
                            ref={importRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                e.target.value = '';
                                if (f) void importFile(f);
                            }}
                        />
                        {onAddProducts && (
                            <Button
                                variant="outline"
                                onClick={onAddProducts}
                                className="mb-1.5 h-auto w-full py-2 text-xs"
                            >
                                {t('CONTENT_STUDIO.MOODBOARD_ADD_PRODUCT', 'Add from catalogue')}
                            </Button>
                        )}
                        <button
                            type="button"
                            onClick={() => importRef.current?.click()}
                            className="mb-3 w-full cursor-pointer rounded-md border-[1.5px] border-dashed bg-muted py-2 text-xs text-muted-foreground transition-colors hover:border-input hover:text-foreground"
                        >
                            {t('CONTENT_STUDIO.MOODBOARD_IMPORT', 'Import or paste an image')}
                        </button>

                        <div className="mb-2 text-[11px] font-medium tracking-wider text-muted-foreground uppercase">
                            {t('CONTENT_STUDIO.MOODBOARD_PIECES', 'Your pieces')}
                        </div>
                        {usableProducts.length === 0 && (
                            <div className="text-[11px] leading-relaxed text-muted-foreground">
                                {t('CONTENT_STUDIO.MOODBOARD_NO_IMAGES', 'These products have no photo to place.')}
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-1.5">
                            {usableProducts.map((p) => (
                                <button
                                    key={p.uuid}
                                    type="button"
                                    onClick={() => addProduct(p)}
                                    title={p.name}
                                    className="cursor-pointer overflow-hidden rounded-lg border bg-background p-0 transition-colors hover:border-input"
                                >
                                    <img
                                        src={srcOf(p)}
                                        alt={p.name}
                                        crossOrigin="anonymous"
                                        className="block h-[62px] w-full bg-muted object-contain"
                                    />
                                    <div className="truncate px-1 py-1 text-[10px] text-muted-foreground">
                                        {p.name}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex min-w-0 flex-1 flex-col">
                        {/* The board's shape is the output's shape, so it is set here. */}
                        <div className="flex items-center gap-3 border-b px-3 py-2">
                            <div className="text-xs font-medium text-muted-foreground">
                                {t('CONTENT_STUDIO.MOODBOARD_SHAPE', 'Shape')}
                            </div>
                            <AspectRatioControl
                                options={aspectOptions?.length ? aspectOptions : FALLBACK_ASPECTS}
                                value={aspectRatio || '3:4'}
                                onChange={(v) => onAspectRatioChange?.(v)}
                            />
                        </div>

                        <div className="flex flex-1 items-center justify-center overflow-hidden bg-muted p-4">
                            <div
                                ref={boardRef}
                                onPointerDown={() => setSelected(null)}
                                className="relative h-full max-w-full overflow-hidden rounded-xl border"
                                style={{ aspectRatio: String(ratio), backgroundColor: BOARD_GROUND }}
                            >
                                {items.map((it) => (
                                    <div
                                        key={it.id}
                                        onPointerDown={(e: React.PointerEvent) => startGesture(e, it.id, 'move')}
                                        className={cn(
                                            'absolute cursor-move touch-none',
                                            selected === it.id && 'outline outline-2 outline-foreground',
                                        )}
                                        style={{
                                            // Pointer maths — board is an LTR coordinate space.
                                            left: `${it.x * 100}%`,
                                            top: `${it.y * 100}%`,
                                            width: `${it.w * 100}%`,
                                            transform: `translate(-50%, -50%) rotate(${it.rot || 0}deg)`,
                                        }}
                                    >
                                        <img
                                            src={it.src}
                                            alt={it.name}
                                            crossOrigin="anonymous"
                                            draggable={false}
                                            className="pointer-events-none block w-full"
                                            style={{ transform: `scale(${it.flipX ? -1 : 1}, ${it.flipY ? -1 : 1})` }}
                                        />
                                        {selected === it.id && (
                                            <>
                                                {/* Rotate — above the piece, so straight up is zero.
                                                    Handle dots keep their literal colour-coding over
                                                    arbitrary imagery (green = rotate, blue = resize). */}
                                                <div
                                                    onPointerDown={(e: React.PointerEvent) => startGesture(e, it.id, 'rotate')}
                                                    title={t('CONTENT_STUDIO.MOODBOARD_ROTATE', 'Rotate') as string}
                                                    className="absolute left-1/2 size-3.5 -translate-x-1/2 cursor-grab touch-none rounded-full"
                                                    style={{ top: -26, backgroundColor: '#10B981', border: '2px solid #FFFFFF' }}
                                                />
                                                {/* One handle, uniform — same contract as the 3D studio. */}
                                                <div
                                                    onPointerDown={(e: React.PointerEvent) => startGesture(e, it.id, 'size')}
                                                    className="absolute size-3.5 cursor-nwse-resize touch-none rounded-full"
                                                    style={{ right: -7, bottom: -7, backgroundColor: '#3B82F6', border: '2px solid #FFFFFF' }}
                                                />
                                            </>
                                        )}
                                    </div>
                                ))}

                                {/**
                                 * The selected piece's actions, in one bar under it.
                                 *
                                 * Outside the rotated element on purpose: buttons that
                                 * turn with the piece end up upside-down, and a delete
                                 * control you have to tilt your head to read is worse
                                 * than one that simply stays put.
                                 */}
                                {selectedItem && (
                                    <div
                                        onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
                                        className="absolute z-[5] flex -translate-x-1/2 items-center gap-0.5 bg-foreground/90 px-1 py-0.5"
                                        style={{
                                            left: `${selectedItem.x * 100}%`,
                                            top: `calc(${selectedItem.y * 100}% + 6px)`,
                                        }}
                                    >
                                        {onRemoveBackground && (
                                            <BarButton
                                                label={t('CONTENT_STUDIO.MOODBOARD_CUTOUT', 'Remove background')}
                                                busy={cutting === selectedItem.id}
                                                onClick={() => void cutOut(selectedItem)}
                                            >
                                                <Wand2 />
                                            </BarButton>
                                        )}
                                        <BarButton
                                            label={t('CONTENT_STUDIO.MOODBOARD_FLIP_H', 'Flip horizontally')}
                                            onClick={() => patch(selectedItem.id, { flipX: !selectedItem.flipX })}
                                        >
                                            <FlipHorizontal2 />
                                        </BarButton>
                                        <BarButton
                                            label={t('CONTENT_STUDIO.MOODBOARD_FLIP_V', 'Flip vertically')}
                                            onClick={() => patch(selectedItem.id, { flipY: !selectedItem.flipY })}
                                        >
                                            <FlipVertical2 />
                                        </BarButton>
                                        <BarButton
                                            label={t('CONTENT_STUDIO.MOODBOARD_FRONT', 'Bring forward')}
                                            onClick={() => move(selectedItem.id, 1)}
                                        >
                                            <ChevronUp />
                                        </BarButton>
                                        <BarButton
                                            label={t('CONTENT_STUDIO.MOODBOARD_BACK', 'Send backward')}
                                            onClick={() => move(selectedItem.id, -1)}
                                        >
                                            <ChevronDown />
                                        </BarButton>
                                        <div className="mx-0.5 h-3.5 w-px bg-background/25" />
                                        <BarButton
                                            label={t('CONTENT_STUDIO.STUDIO_REMOVE', 'Remove')}
                                            danger
                                            onClick={() => removeItem(selectedItem.id)}
                                        >
                                            <Trash2 />
                                        </BarButton>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-3 border-t p-3">
                            <div className={cn(
                                'flex-1 text-[11px] leading-snug',
                                error ? 'text-destructive' : 'text-muted-foreground',
                            )}>
                                {error || t(
                                    'CONTENT_STUDIO.MOODBOARD_HINT',
                                    'Drag to arrange. Pieces higher on the board are read as further back in the room.',
                                )}
                            </div>
                            <Button variant="outline" onClick={() => setOpen(false)}>
                                {t('CONTENT_STUDIO.CANCEL', 'Cancel')}
                            </Button>
                            <Button
                                variant="outline"
                                disabled={busy || items.length === 0}
                                onClick={captureBoard}
                                className="min-w-[120px]"
                            >
                                {busy
                                    ? t('CONTENT_STUDIO.MOODBOARD_SAVING', 'Saving…')
                                    : t('CONTENT_STUDIO.MOODBOARD_USE', 'Use this board')}
                            </Button>
                        </div>
                    </div>

                    {/* Layers — front at the top, the way a layers panel reads. */}
                    <div className="w-[178px] shrink-0 overflow-y-auto border-s p-3">
                        <div className="mb-2 text-[11px] font-medium tracking-wider text-muted-foreground uppercase">
                            {t('CONTENT_STUDIO.MOODBOARD_LAYERS', 'Layers')}
                        </div>
                        {items.length === 0 && (
                            <div className="text-[11px] leading-relaxed text-muted-foreground">
                                {t('CONTENT_STUDIO.MOODBOARD_LAYERS_EMPTY', 'Nothing on the board yet.')}
                            </div>
                        )}
                        {[...items].reverse().map((it) => (
                            <div
                                key={it.id}
                                onClick={() => setSelected(it.id)}
                                className={cn(
                                    'mb-1 flex cursor-pointer items-center gap-1 rounded-lg border bg-background p-1',
                                    selected === it.id && 'border-foreground',
                                )}
                            >
                                <img
                                    src={it.src}
                                    alt=""
                                    crossOrigin="anonymous"
                                    className="size-[26px] shrink-0 rounded-sm bg-muted object-contain"
                                />
                                <div className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">
                                    {it.name}
                                </div>
                                <div className="flex flex-col">
                                    <button
                                        type="button"
                                        onClick={(e: React.MouseEvent) => { e.stopPropagation(); move(it.id, 1); }}
                                        title={t('CONTENT_STUDIO.MOODBOARD_FRONT', 'Bring forward') as string}
                                        className="cursor-pointer border-0 bg-transparent p-0 leading-none text-muted-foreground hover:text-foreground"
                                    >
                                        <ChevronUp className="size-[13px]" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(e: React.MouseEvent) => { e.stopPropagation(); move(it.id, -1); }}
                                        title={t('CONTENT_STUDIO.MOODBOARD_BACK', 'Send backward') as string}
                                        className="cursor-pointer border-0 bg-transparent p-0 leading-none text-muted-foreground hover:text-foreground"
                                    >
                                        <ChevronDown className="size-[13px]" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};
